import { Inject, Injectable } from '@angular/core';
import { JobsQueryRequest } from './models/jobs-query-request';
import { JobsQueryResponse } from './models/jobs-query-response';
import {
    HttpClient,
    HttpErrorResponse,
    HttpResponse,
} from '@angular/common/http';
import {
    StandardRequestParameters,
    RequestParametersDictionary,
} from '../common/models/standard-request-parameters';
import { TokenManager } from '../../../utils/token-manager';
import { GoogleApiError } from '../common/models/google-api-error';
import { processRequest } from '../common/utils/request-utils';
import { JobsQueryResultsResponse } from './models/jobs-query-results-response';
import { JobsQueryResultsParameters } from './models/jobs-query-results-parameters';
import { catchError, of } from 'rxjs';

/**
 * Service that wraps BigQuery REST API.
 * TODO: Add support for other BigQuery API methods.
 */
@Injectable({
    providedIn: 'root',
})
export class BigQueryService {
    public static BaseUrl = 'https://bigquery.googleapis.com/bigquery/v2/';

    private readonly tokenManager: TokenManager;

    /**
     * Constructor requires custom provide factory in app module.
     * https://cloud.google.com/bigquery/docs/reference/rest/v2/jobs/getQueryResults
     * @param http HttpClient to use for requests.
     * @param accessTokenGetter Getter for access token to use for requests. It is called only when there is no token or token is expired.
     */
    constructor(
        private http: HttpClient,
        @Inject(() => Promise<string>) accessTokenGetter: () => Promise<string>
    ) {
        this.tokenManager = new TokenManager(accessTokenGetter);
    }

    public async jobsGetQueryResults(
        project: string,
        jobId: string,
        requestParameters?: JobsQueryResultsParameters,
        standardParameters?: StandardRequestParameters
    ): Promise<JobsQueryResultsResponse> {
        return await processRequest(
            async () =>
                this.http.get<JobsQueryResultsResponse>(
                    `${BigQueryService.BaseUrl}projects/${project}/queries/${jobId}`,
                    {
                        params: {
                            ...standardParameters,
                            ...requestParameters,
                        } as RequestParametersDictionary,
                        headers: {
                            Authorization: `Bearer ${await this.tokenManager.getToken()}`,
                        },
                        observe: 'response',
                    }
                ),
            async () => {
                await this.tokenManager.refreshToken();
            }
        );
    }

    /**
     * Runs a BigQuery SQL query synchronously and returns query results if the query completes within a specified timeout.
     * https://cloud.google.com/bigquery/docs/reference/rest/v2/jobs/query
     * @param project Required. Project ID of the query request.
     * @param request The request body contains an instance of {@link JobsQueryRequest}.
     * @param requestParameters Optional request parameters to apply.
     * @returns If successful, the response body contains data with the {@link JobsQueryResponse} structure. Otherwise promise is rejected with {@link GoogleApiError}
     */
    public async jobsQuery(
        project: string,
        request: JobsQueryRequest,
        requestParameters?: StandardRequestParameters
    ): Promise<JobsQueryResponse> {
        return await processRequest(
            async () =>
                this.http
                    .post<JobsQueryResponse>(
                        `${BigQueryService.BaseUrl}projects/${project}/queries`,
                        request,
                        {
                            params: requestParameters as RequestParametersDictionary,
                            headers: {
                                Authorization: `Bearer ${await this.tokenManager.getToken()}`,
                            },
                            observe: 'response',
                        }
                    )
                    .pipe(
                        catchError((err: HttpErrorResponse) =>
                            // redirect error response to underlying method
                            of(err)
                        )
                    ),
            async () => {
                await this.tokenManager.refreshToken();
            }
        );
    }
}
